// ManiDine Restaurant Portal / src/RestaurantDashboard.jsx / Updated: 2026-04-02 16:55 America/Edmonton
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import CustomerReviewsPage from "./CustomerReviewsPage.jsx";
import ManagerShell from "./components/ManagerShell";
import { db } from "./firebase";
import useRestaurantContext from "./hooks/useRestaurantContext";
import "./Dashboard.css";

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const deriveNumericCode = (order) => {
  const raw = order.orderNumber || order.id || "";
  const digitsOnly = raw.replace(/\D/g, "");
  if (digitsOnly) {
    const trimmed = digitsOnly.slice(-5);
    return trimmed.padStart(4, "0");
  }
  if (!raw) return "";
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) % 100000;
  }
  return String(hash).padStart(4, "0");
};

const getOrderDate = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getOrderTotal = (order) => {
  if (typeof order.total === "number") return order.total;
  if (typeof order.totalAmount === "number") return order.totalAmount;
  if (typeof order.subtotal === "number") return order.subtotal;
  return 0;
};

const parseNumber = (value, fallback = 0) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

const getOrderFoodSubtotal = (order) => {
  if (typeof order?.subtotal === "number") return order.subtotal;
  if (typeof order?.itemSubtotal === "number") return order.itemSubtotal;
  if (typeof order?.itemsSubtotal === "number") return order.itemsSubtotal;
  if (typeof order?.foodSubtotal === "number") return order.foodSubtotal;

  const total =
    typeof order?.totalAmount === "number"
      ? order.totalAmount
      : typeof order?.total === "number"
      ? order.total
      : null;

  if (typeof total === "number") {
    const tax = parseNumber(order?.taxAmount ?? order?.tax);
    const deliveryFee = parseNumber(order?.deliveryFee);
    const tip =
      typeof order?.tipAmount === "number"
        ? order.tipAmount
        : typeof order?.tip === "number"
        ? order.tip
        : 0;

    return Math.max(total - tax - deliveryFee - tip, 0);
  }

  return 0;
};

const getOrderTax = (order) => parseNumber(order?.taxAmount ?? order?.tax);

const getRestaurantSalesTotal = (order) =>
  getOrderFoodSubtotal(order) + getOrderTax(order);

const isRestaurantSalesEligible = (status) => {
  const s = String(status || "").toLowerCase().trim();
  if (!s) return true;

  return !(
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void") ||
    s.includes("declin")
  );
};

const normalize = (v) => String(v ?? "").toLowerCase().trim();

const RestaurantDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeView = searchParams.get("view") || "overview";
  const isCustomersView = activeView === "customers";
  const ordersSectionRef = useRef(null);

  const [restaurant, setRestaurant] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const [summary, setSummary] = useState({ sales: 0, count: 0, avg: 0 });
  const [recentOrders, setRecentOrders] = useState([]);

  // ✅ If the context loader gets stuck, we show a helpful fallback instead of infinite loading
  const [loadingTooLong, setLoadingTooLong] = useState(false);

  const {
    restaurantId,
    restaurant: restaurantProfile,
    loading: restaurantLoading,
  } = useRestaurantContext() || {};

  useEffect(() => {
    setRestaurant(restaurantProfile || null);
    setProfileLoading(restaurantLoading);
  }, [restaurantProfile, restaurantLoading]);

  useEffect(() => {
    setLoadingTooLong(false);

    if (!restaurantLoading) return undefined;

    const t = setTimeout(() => {
      setLoadingTooLong(true);
    }, 6000);

    return () => clearTimeout(t);
  }, [restaurantLoading]);

  useEffect(() => {
    if (restaurantLoading) {
      setOrdersLoading(true);
      return;
    }

    // ✅ When restaurantId is not ready yet, DO NOT crash.
    // Just reset visible dashboard state.
    if (!restaurantId) {
      setRecentOrders([]);
      setSummary({ sales: 0, count: 0, avg: 0 });
      setOrdersError("");
      setOrdersLoading(false);
      return;
    }

    const getDate = (doc) =>
      typeof doc.createdAt?.toDate === "function"
        ? doc.createdAt.toDate()
        : doc.createdAt
        ? new Date(doc.createdAt)
        : new Date(0);

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    console.log("Loading orders for restaurant", restaurantId);

    const ordersRef = collection(db, "orders");
    const ordersQuery = query(
      ordersRef,
      where("restaurantId", "==", restaurantId)
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        console.log("[Dashboard] Orders snapshot size:", snapshot.size);
        const docs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        docs.sort((a, b) => getDate(b) - getDate(a));

        const todaysEligibleOrders = docs.filter((order) => {
          const createdAt = getOrderDate(order.createdAt);
          if (!createdAt || createdAt < startOfToday) return false;
          return isRestaurantSalesEligible(order.status);
        });

        const totalSales = todaysEligibleOrders.reduce(
          (sum, order) => sum + getRestaurantSalesTotal(order),
          0
        );
        const orderCount = todaysEligibleOrders.length;
        const avgTicket = orderCount ? totalSales / orderCount : 0;

        const sorted = docs.sort((a, b) => {
          const aDate = getOrderDate(a.createdAt) || 0;
          const bDate = getOrderDate(b.createdAt) || 0;
          return bDate - aDate;
        });

        setSummary({ sales: totalSales, count: orderCount, avg: avgTicket });
        setRecentOrders(sorted.slice(0, 5));
        setOrdersError("");
        setOrdersLoading(false);
      },
      (err) => {
        console.error("Error loading orders:", err);
        setOrdersError("We couldn't load your orders. Please try again.");
        setOrdersLoading(false);
      }
    );

    return () => unsubscribe();
  }, [restaurantId, restaurantLoading]);

  const greetingName =
    restaurant?.contactName ||
    [restaurant?.firstName, restaurant?.lastName].filter(Boolean).join(" ") ||
    restaurant?.restaurantName ||
    "there";

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "morning";
    if (hour < 17) return "afternoon";
    return "evening";
  };

  const formatCurrency = (value) => currencyFormatter.format(value || 0);

  const formatTime = (timestamp) => {
    const date = getOrderDate(timestamp);
    if (!date) return "--:--";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleQuickAction = (action) => {
    if (action === "orders") {
      if (ordersSectionRef.current) {
        ordersSectionRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/dashboard");
      }
      return;
    }

    if (action === "menu") {
      navigate("/menu");
      return;
    }

    if (action === "settings") {
      navigate("/settings");
    }
  };

  // ✅ 1) If context is still loading — show a non-stuck loader (with fallback hint)
  if (restaurantLoading) {
    return (
      <ManagerShell
        title="Dashboard"
        subtitle="Here's what's happening with your restaurant today."
      >
        <div className="dashboard-loading" style={{ maxWidth: 720 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Loading restaurant…
          </div>

          {!loadingTooLong ? (
            <div className="muted-text">
              Please wait a moment.
            </div>
          ) : (
            <div className="muted-text">
              This is taking longer than expected. Try refreshing the page.
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </button>
              </div>
            </div>
          )}
        </div>
      </ManagerShell>
    );
  }

  // ✅ 2) If no restaurantId at all (not signed in properly)
  if (!restaurantId) {
    return (
      <ManagerShell
        title="Dashboard"
        subtitle="Here's what's happening with your restaurant today."
      >
        <div className="dashboard-loading">
          No restaurant found. Please sign in again.
        </div>
      </ManagerShell>
    );
  }

  // ✅ 3) If restaurant profile exists but is not approved, show a pending screen (NOT loading)
  const onboardingStatus = normalize(restaurant?.onboardingStatus);
  const isApproved = onboardingStatus === "approved";
  const isPending = onboardingStatus === "pending_review" || onboardingStatus === "pending";

  if (restaurant && !isApproved) {
    return (
      <ManagerShell
        title={`Good ${getGreeting()}, ${greetingName}`}
        subtitle="Your account status"
      >
        <div className="dashboard-main-grid">
          <div className="dashboard-left-column">
            <div
              className="dashboard-error"
              style={{
                background: "#fff",
                border: "1px solid rgba(15,23,42,0.08)",
                borderRadius: 16,
                padding: 18,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
                {isPending ? "Pending approval" : "Account restricted"}
              </div>

              <div className="muted-text" style={{ lineHeight: 1.5 }}>
                {isPending
                  ? "Thanks for signing up. Your restaurant is currently under review. Once approved, you’ll be able to access Orders, Menu, and Payments."
                  : "Your account is not approved yet. Please contact support or check your onboarding status in Firestore."}
              </div>

              <div style={{ marginTop: 12 }}>
                <div className="muted-text" style={{ fontSize: 13 }}>
                  Current status:{" "}
                  <strong>{restaurant.onboardingStatus || "unknown"}</strong>
                </div>
              </div>

              <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </button>
              </div>

              <div className="muted-text" style={{ marginTop: 10, fontSize: 13 }}>
                Settings will become available after your restaurant is approved.
              </div>
            </div>
          </div>
        </div>
      </ManagerShell>
    );
  }

  // ✅ 4) If profile is missing (context returned null) — show a clear message instead of loading forever
  if (!restaurant && !profileLoading) {
    return (
      <ManagerShell
        title="Dashboard"
        subtitle="Here's what's happening with your restaurant today."
      >
        <div className="dashboard-loading" style={{ maxWidth: 720 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>
            Restaurant profile not found
          </div>
          <div className="muted-text">
            We found your login, but could not load your restaurant profile.
            This is usually a Firestore permission rule issue or the restaurant
            document is missing.
          </div>

          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="text-button"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        </div>
      </ManagerShell>
    );
  }

  const isLoading = profileLoading || ordersLoading;

  return (
    <ManagerShell
      title={`Good ${getGreeting()}, ${greetingName}`}
      subtitle="Here's what's happening with your restaurant today."
    >
      {isCustomersView ? (
        <CustomerReviewsPage embedded />
      ) : isLoading ? (
        <div className="dashboard-loading">Loading dashboard…</div>
      ) : (
        <div className="dashboard-main-grid">
          <div className="dashboard-left-column">
            {ordersError && <div className="dashboard-error">{ordersError}</div>}

            <section className="summary-section">
              <div className="summary-header">
                <div>
                  <h2>Today's summary</h2>
                  <p className="muted-text" style={{ marginTop: 6 }}>
                    Restaurant sales only: food subtotal + tax. Delivery fees and tips are excluded.
                  </p>
                </div>
                <p className="summary-date">
                  {new Date().toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>

              <div className="summary-cards">
                <div className="summary-card">
                  <p className="summary-label">Sales</p>
                  <h3 className="summary-value">{formatCurrency(summary.sales)}</h3>
                </div>

                <div className="summary-card">
                  <p className="summary-label">Orders</p>
                  <h3 className="summary-value">{summary.count}</h3>
                </div>

                <div className="summary-card">
                  <p className="summary-label">Average ticket size</p>
                  <h3 className="summary-value">{formatCurrency(summary.avg)}</h3>
                </div>
              </div>
            </section>

            <section className="recent-orders" id="recent-orders" ref={ordersSectionRef}>
              <div className="section-heading">
                <h3>Recent orders</h3>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => handleQuickAction("orders")}
                >
                  View all
                </button>
              </div>

              {ordersLoading ? (
                <p className="muted-text">Loading orders…</p>
              ) : recentOrders.length === 0 ? (
                <p className="muted-text">No orders yet today.</p>
              ) : (
                <ul className="orders-list">
                  {recentOrders.map((order) => {
                    const status = (order.status || "pending").toLowerCase();
                    const total = getOrderTotal(order);
                    const code = deriveNumericCode(order);

                    return (
                      <li key={order.id} className="order-row">
                        <div className="order-row-main">
                          <span className="order-id">#{code}</span>
                          <span className={`status-pill ${status}`}>
                            {order.status || "pending"}
                          </span>
                        </div>

                        <div className="order-row-meta">
                          <span className="order-amount">{formatCurrency(total)}</span>
                          <span className="order-time">{formatTime(order.createdAt)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </ManagerShell>
  );
};

export default RestaurantDashboard;
